import { useEffect } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useShallow } from "zustand/react/shallow";

import { RootState } from "store/rootReducer";
import {
  selectSelectedWells,
  selectTxnId,
  selectXdaLineRequest,
  useMapStore
} from "stores/map";

import { fetchDimensionsFromPlay, fetchRadiusDimensions } from "api/ipdp";
import { fetchDefaultSizesForXDA } from "api/map";

import { useXdaSettingsStore } from "../context";
import { XDA_RADIUS_TYPES } from "../context/types";
import { selectXdaSettings } from "../context/xdaSettingsSelectors";

/**
 * Simple `hook` that abstracts API call that fetched default size/constraints for selected wells on Map
 */
const useXdaConstraint = () => {
  const settings = useXdaSettingsStore(useShallow(selectXdaSettings));
  const setXdaSettings = useXdaSettingsStore((s) => s.setXdaSettings);
  const { isUsingResourcePlayDefault, selectedRadius } = settings;
  const checkedPadScenarios = useSelector(
    (state: RootState) => state.app.checkedPadScenarios
  );

  const txnId = useMapStore(selectTxnId);
  const selectedWells = useMapStore(selectSelectedWells);
  const xdaLineRequest = useMapStore(selectXdaLineRequest);

  // In group mode selectedWells is always empty (the map renders synthetic group
  // entities, not individual wells). Fall back to the wells intersecting the drawn
  // XDA line so that fetchDefaultSizesForXDA fires with real UWIs and dimensions
  // are non-zero when the group-mode XDA payload is built.
  const selectedWellKeys = Object.keys(selectedWells);
  const uwiList: string[] =
    selectedWellKeys.length > 0 ? selectedWellKeys : (xdaLineRequest.wells as string[]);

  // query params
  // txnId/uwiList added to queryKey will make sure query is executed again on changes, as long as enabled resolves to true.
  const queryKey = [
    "xdaConstraint",
    txnId?.id,
    ...uwiList,
    selectedRadius,
    ...checkedPadScenarios.map((s) => s.scenarioId)
  ];
  const queryFn = () =>
    // If there is no selected wells, we should try to load dimensions from pad scenario play
    !uwiList?.length && checkedPadScenarios.length > 0 && !!checkedPadScenarios[0]?.play
      ? fetchDimensionsFromPlay(checkedPadScenarios[0].play)
      : selectedRadius === XDA_RADIUS_TYPES.Custom
      ? fetchDefaultSizesForXDA(uwiList)
      : fetchRadiusDimensions(uwiList);

  // org_* and job preset types manage their own dimensions via handleRadiusChange
  const isBuiltInType = Object.values(XDA_RADIUS_TYPES).includes(
    selectedRadius as XDA_RADIUS_TYPES
  );

  // query fires as wells are selected on Map. It runs for every radius type
  // (not just built-in ones) so the play under the line always resolves — the
  // radius dropdown needs it to validate org presets. Mesh dimensions are only
  // applied for built-in types below.
  const { data, refetch } = useQuery(queryKey, queryFn, {
    enabled: !!uwiList.length || checkedPadScenarios.length > 0
  });

  // sync fetched defaults with XDA settings in context
  useEffect(() => {
    if (!data) {
      //If there is no data to set the radius then it should be 0
      if (
        isBuiltInType &&
        (settings.upHeight != 0 ||
          settings.downHeight != 0 ||
          settings.leftWidth != 0 ||
          settings.rightWidth != 0) &&
        isUsingResourcePlayDefault
      ) {
        setXdaSettings({
          ...settings,
          upHeight: 0,
          downHeight: 0,
          leftWidth: 0,
          rightWidth: 0
        });
      }
      return;
    }

    // Track the play of the wells under the XDA line for use in org preset matching
    const activeXdaPlay = data.play ?? settings.activeXdaPlay;

    // org_* and job presets manage their own dimensions via handleRadiusChange;
    // only keep the active play in sync so the radius dropdown can validate org
    // options (and revert to an EVA Research radius when they don't apply).
    if (!isBuiltInType) {
      if (activeXdaPlay !== settings.activeXdaPlay) {
        setXdaSettings({ ...settings, activeXdaPlay });
      }
      return;
    }

    // Handle non-custom radius types with specific dimension data
    if (selectedRadius !== XDA_RADIUS_TYPES.Custom && data.dimensions) {
      const targetDimensions = data.dimensions.find((d) => d.name === selectedRadius);

      if (targetDimensions) {
        const {
          upHeight = 50,
          downHeight = 50,
          leftWidth = 300,
          rightWidth = 300
        } = targetDimensions;

        setXdaSettings({
          ...settings,
          activeXdaPlay,
          upHeight,
          downHeight,
          leftWidth,
          rightWidth
        });
      }
      return;
    }
    const {
      XDAWidth,
      XDAHeight,
      xdaHeightDown,
      xdaHeightUp,
      xdaWidthLeft,
      xdaWidthRight
    } = data;

    if (isUsingResourcePlayDefault) {
      const newUpHeight = xdaHeightUp ?? XDAHeight ?? 50;
      const newDownHeight = xdaHeightDown ?? XDAHeight ?? 50;
      const newLeftWidth = xdaWidthLeft ?? XDAWidth ?? 300;
      const newRightWidth = xdaWidthRight ?? XDAWidth ?? 300;
      setXdaSettings({
        ...settings,
        activeXdaPlay,
        upHeight: newUpHeight,
        downHeight: newDownHeight,
        leftWidth: newLeftWidth,
        rightWidth: newRightWidth
      });
    } else {
      setXdaSettings({
        ...settings,
        activeXdaPlay,
        upHeight: settings.upHeight,
        downHeight: settings.downHeight,
        leftWidth: settings.leftWidth,
        rightWidth: settings.rightWidth
      });
    }
  }, [data, isUsingResourcePlayDefault, selectedRadius, isBuiltInType]);

  return {
    playConstraint: data,
    refetchPlayConstraint: refetch
  };
};

export default useXdaConstraint;
